import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  REQUIRED_LISTING_FIELD_LABELS,
  type RequiredListingField,
} from '../../../../utils/storeListingCompleteness';
import { FORM_ID } from '../constants';

export function MissingRequiredFields({
  fields,
}: {
  fields: RequiredListingField[];
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedField, setSelectedField] =
    useState<RequiredListingField | null>(null);

  useEffect(() => {
    if (!selectedField || searchParams.get('tab') === 'editors-choice') return;

    const form = document.getElementById(FORM_ID);
    const input = form?.querySelector<HTMLElement>(
      selectedField === 'dappPreviews'
        ? '[data-listing-field="dappPreviews"]'
        : `[name="${selectedField}"]`,
    );
    const target =
      selectedField === 'dappPreviews'
        ? input?.querySelector<HTMLElement>('[role="button"]')
        : input?.matches('input[type="hidden"], input[type="file"]')
          ? (
              input.closest('label') ?? input.parentElement
            )?.querySelector<HTMLElement>(
              '[role="button"], button[aria-haspopup]',
            )
          : input;
    target?.scrollIntoView({ block: 'center' });
    target?.focus({ preventScroll: true });
    setSelectedField(null);
  }, [selectedField, searchParams]);

  if (fields.length === 0) return null;

  return (
    <section
      aria-label="Missing required fields"
      aria-live="polite"
      className="rounded-lg border border-orange-400/30 bg-orange-400/5 px-4 py-3 text-sm"
    >
      <p className="font-medium text-orange-400">
        {fields.length === 1
          ? 'Missing required field'
          : 'Missing required fields'}
      </p>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
        {fields.map((field) => (
          <li key={field}>
            <button
              type="button"
              className="rounded text-left text-orange-400 underline underline-offset-4 hover:text-orange-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
              onClick={() => {
                setSearchParams(
                  (previous) => {
                    const next = new URLSearchParams(previous);
                    next.delete('tab');
                    return next;
                  },
                  { replace: true },
                );
                setSelectedField(field);
              }}
            >
              {REQUIRED_LISTING_FIELD_LABELS[field]}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
